import { parseAuthResult } from "./authentication/amplify/amplify.helpers";
import {
  ADMIN_MFA_REQUIRED,
  TWO_FACTOR_PATH,
} from "./authentication/two-factor";
import { Auth } from "./authentication";

const OMG_API_URL = import.meta.env.VITE_OMG_API_URL;

async function getAuthHeaders(): Promise<Record<string, string>> {
  const { accessToken } = parseAuthResult(
    await Auth.currentAuthenticatedUser(),
  );
  return {
    Authorization: `Bearer ${accessToken?.jwt}`,
    "Content-Type": "application/json",
  };
}

// omg-api errors are JSON `{ name, message }`, and the BE (OMG-2090/2091) builds
// actionable messages on purpose (duplicate pricing, existing SKU, entitlement
// still held by a customer, CUSTOMER_EXISTS). Surface that message so the admin
// sees what to fix; fall back to the status line when there's no JSON body.
async function throwForStatus(response: Response): Promise<never> {
  let message = `API error: ${response.status} ${response.statusText}`;
  try {
    const body = await response.json();
    if (body && typeof body.message === "string" && body.message) {
      message = body.message;
    }
    // omg-api refuses admins without two-factor sign-in once ADMIN_MFA is
    // enforced (OMG-2777): send them to set it up.
    if (response.status === 403 && body?.code === ADMIN_MFA_REQUIRED) {
      window.location.assign(TWO_FACTOR_PATH);
    }
  } catch {
    // no parseable body — keep the status-line fallback
  }
  throw new Error(message);
}

// Read a JSON body, tolerating an empty 204 No Content (set-password, delete).
async function readBody<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T;
  }
  return response.json();
}

export async function adminGet<T>(path: string): Promise<T> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${OMG_API_URL}${path}`, { headers });
  if (!response.ok) {
    await throwForStatus(response);
  }
  return readBody<T>(response);
}

export async function adminPatch<T>(
  path: string,
  body: Record<string, unknown>,
): Promise<T> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${OMG_API_URL}${path}`, {
    method: "PATCH",
    headers,
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    await throwForStatus(response);
  }
  return readBody<T>(response);
}

export async function adminPost<T>(
  path: string,
  body?: Record<string, unknown>,
): Promise<T> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${OMG_API_URL}${path}`, {
    method: "POST",
    headers,
    ...(body && { body: JSON.stringify(body) }),
  });
  if (!response.ok) {
    await throwForStatus(response);
  }
  return readBody<T>(response);
}

export async function adminDelete(path: string): Promise<void> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${OMG_API_URL}${path}`, {
    method: "DELETE",
    headers,
  });
  if (!response.ok) {
    await throwForStatus(response);
  }
  // Admin deletes return 204 No Content.
}
